/**
 * DashboardCall Component
 * Integrates Whereby video calling for team collaboration
 * Shared component used by both gamer and facilitator features
 * Updated to match transition-web implementation
 */

import React, { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { DashboardCallEmptyState } from './DashboardCallEmptyState'
import { WherebyEmbed } from './WherebyEmbed'
import { WherebyCallBlockedPanel } from '../../../features/gamer/components/whereby/WherebyCallBlockedPanel'
import { useWherebyCallOwnership } from '../../hooks/useWherebyCallOwnership'
import { buildEmbeddedCallScope } from '../../hooks/wherebyCallScopes'
import { useUser } from '../../../hooks/useUser'
import {
  useTeamWherebyMeeting,
  useActivityWherebyMeeting,
  useCreateWherebyMeeting,
  useEnsureActivityWherebyMeeting
} from '../../../apiLayer'
import useAppStateSession from '../../../hooks/useAppStateSession'
import useAppState from '../../../hooks/useAppState'
import { useSessionId } from '../../../hooks/useSessionId'
import { resolveWherebyScope } from '../../../../../pure-js/libs/resolveWherebyScope'
import { getWherebyCallDisplayName } from '../../../../../pure-js/libs/isWherebyBotParticipant'
import { useSessionIdFacilitator } from '../../../hooks/useSessionIdFacilitator'
import { OrgRole } from '../../../../../pure-js/types/OrganizationMembership'
import { getActiveOrgRole } from '../../../features/organization/orgStateHelpers'

// Constants for panel dimensions
export const DASHBOARD_CALL_PANEL_WIDTH = 480
export const DASHBOARD_CALL_PANEL_MIN_HEIGHT = 400

interface DashboardCallProps {
  width?: string | number
  height?: string | number
  forceShow?: boolean // Bypasses shouldShowCall check (for Game Center)
}

export const DashboardCall: React.FC<DashboardCallProps> = ({ width = '100%', height, forceShow = false }) => {
  const { state: sessionState } = useAppStateSession()
  const location = useLocation()
  const { data: user } = useUser()
  const { state: appState } = useAppState()
  const orgRole = getActiveOrgRole(user)

  // Check if we're on a facilitator route (for both FACILITATOR and ADMIN users)
  const isFacilitatorRoute = location.pathname.startsWith('/facilitator')
  const isFacilitatorUser = orgRole === OrgRole.FACILITATOR
  const shouldUseFacilitatorHook = isFacilitatorRoute || isFacilitatorUser

  // Conditionally call hooks based on route/user type to prevent unnecessary gamer API calls
  const gamerSession = useSessionId({ skip: shouldUseFacilitatorHook })
  const facilitatorSession = useSessionIdFacilitator()

  const session = shouldUseFacilitatorHook ? facilitatorSession : gamerSession
  const { teamId, sprintId, activityId, activityType, isLoading: isLoadingSession } = session

  // Shared activity-vs-team scope decision (resolveWherebyScope) — identical rule as the floating call,
  // so a game always gets its own room and never the shared team room.
  const { isActivityScope, teamRoomTeamId } = resolveWherebyScope({
    sprintId,
    activityId,
    activityType,
    teamId
  })
  const activityQuery = useActivityWherebyMeeting(isActivityScope ? { teamId, activityId } : undefined)
  // An activity with a room of its own (a game, a debrief, a sprint call) never lands in the team room.
  const teamQuery = useTeamWherebyMeeting(teamRoomTeamId)

  const activeMeeting = (isActivityScope ? activityQuery.data : teamQuery.data)?.data ?? undefined
  const isLoadingMeeting = isActivityScope ? activityQuery.isLoading : teamQuery.isLoading
  const refetchMeeting = isActivityScope ? activityQuery.refetch : teamQuery.refetch

  const isLoading = isLoadingSession || isLoadingMeeting

  // roomUrl only — hostRoomUrl grants host privileges and is reserved for the AI assistant, not regular
  // participants. (Matches WherebyCallSurface / useWherebyRoom; previously this used hostRoomUrl first.)
  const room = activeMeeting?.roomUrl
  // The signed-in user stands in when the user read came back empty.
  const displayName = getWherebyCallDisplayName(user) || getWherebyCallDisplayName(appState.user)

  const createWherebyMeetingMutation = useCreateWherebyMeeting()
  const ensureActivityMutation = useEnsureActivityWherebyMeeting()

  // Auto-create the room (per-activity or team) if none is active.
  useEffect(() => {
    if (isLoading || !teamId) return
    if (activeMeeting) return

    if (isActivityScope && sprintId && activityId) {
      ensureActivityMutation.mutate({ teamId, sprintId, activityId }, { onSuccess: () => void refetchMeeting() })
    } else if (teamRoomTeamId) {
      createWherebyMeetingMutation.mutate(
        { roomNamePrefix: teamRoomTeamId, teamId: teamRoomTeamId },
        { onSuccess: () => void refetchMeeting() }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, sprintId, activityId, isLoading, activeMeeting])

  // One live Whereby connection per user: this embed goes through the same claim as the floating call, so
  // it can never put the user in a second room (not even in the same tab).
  const wantsToBeInTheCall = Boolean((forceShow || sessionState.shouldShowCall) && room)
  const ownershipScope = useMemo(
    () =>
      buildEmbeddedCallScope({
        teamId,
        sprintId,
        activityId,
        activityType,
        isActivityScope,
        currentPathname: location.pathname
      }),
    [teamId, sprintId, activityId, activityType, isActivityScope, location.pathname]
  )
  const ownership = useWherebyCallOwnership({ enabled: wantsToBeInTheCall, scope: ownershipScope })

  // Show empty state if call is not requested (unless forceShow is true)
  if (!forceShow && !sessionState.shouldShowCall) {
    return <DashboardCallEmptyState teamId={teamId} />
  }

  // Check config flag (matches transition-web behavior)
  // if (!config.enableCall) {
  //   return null
  // }

  // If call is requested but no room yet, return null (matches transition-web behavior)
  if (!room) {
    return null
  }

  // No embed until the person's own name is known.
  if (!displayName) {
    return null
  }

  // The call is live somewhere else (another tab/device, or the floating call in this tab) — ask, never
  // connect on top of it.
  if (!ownership.canConnect) {
    return (
      <WherebyCallBlockedPanel
        status={ownership.status}
        onConfirmTakeover={ownership.confirmTakeover}
        onRetry={ownership.retry}
      />
    )
  }

  // Render Whereby embed directly
  return <WherebyEmbed room={room} displayName={displayName} width={width} height={height} />
}
